import type { ReactNode } from "react";
import {
  Image,
  getSrcSet,
  type FitOptions,
  type ImageProps,
} from "@decocms/blocks/hooks";

export interface PictureSourceProps {
  src: string;
  width: number;
  height?: number;
  media: string;
  fit?: FitOptions;
  sizes?: string;
}

export interface PictureProps extends Omit<ImageProps, "sizes"> {
  sources: PictureSourceProps[];
  /**
   * Classes aplicadas ao `<picture>` (o wrapper), não ao `<img>`.
   * `<picture>` é `display:inline` e de altura automática por padrão, então
   * um `h-full` no `<img>` não tem contra o que resolver os 100% e a imagem
   * acaba com a altura natural da sua proporção — dentro de uma caixa de
   * altura fixa isso sobra como vão vazio (ou transborda). Quem precisa que
   * a imagem preencha a caixa passa aqui algo como `block w-full h-full`.
   */
  pictureClassName?: string;
}

export function Source(props: PictureSourceProps & { fit?: FitOptions }) {
  // Arte servida pelo próprio site (`public/`, portanto um caminho começando
  // com "/") não passa pelo CDN de imagem: `getSrcSet` monta
  // `decoims.com/image?...&src=/caminho`, e o CDN resolve esse caminho
  // relativo contra o domínio DELE — 404, imagem quebrada. Nesses casos o
  // caminho vai direto, servido na mesma origem.
  const srcSet = props.src.startsWith("/")
    ? props.src
    : getSrcSet(props.src, props.width, props.height, props.fit ?? "cover");
  return (
    <source
      srcSet={srcSet}
      media={props.media}
      width={props.width}
      height={props.height}
      sizes={props.sizes ?? `${props.width}px`}
    />
  );
}

export function Picture({
  sources,
  src,
  width,
  height,
  fit = "cover",
  preload,
  children,
  pictureClassName,
  ...rest
}: PictureProps & { children?: ReactNode }) {
  return (
    <picture className={pictureClassName}>
      {children ?? sources?.map((source, i) => (
        <Source key={i} {...source} fit={source.fit ?? fit} />
      ))}
      {src && <Image src={src} width={width} height={height} fit={fit} preload={preload} {...rest} />}
    </picture>
  );
}
